import { signal } from '@preact/signals';
import type { Game } from '../../game/Game';
import type { ZoneRuntime } from '../../game/ZoneRuntime';
import { type SeaHudState, activeSea, setupSailingZone } from './runtime';
import { SeaScene } from './scene';
import { shipOf, statsOf } from './ship';

/**
 * Browser-side glue: attaches the sea presentation, runs it every frame, feeds
 * the sea HUD, pulls the camera out for the helm view (restored ashore) and
 * performs travel the sea script requested during the simulation step.
 */

export interface SeaHudView extends SeaHudState {
  /** Smoothed weather at the helm (drives the screen vignettes). */
  vis: { storm: number; fog: number };
  /** Seconds since boarding (first-voyage hints). */
  voyage: number;
}

export const seaHud = signal<SeaHudView | null>(null);
export const sailGame: { current: Game | null } = { current: null };

/** Helm camera: much further out than the combat camera (a 12 m hull, islands on the horizon). */
const SEA_CAM = { near: { dist: 30, pitch: 44, lookY: 1.6 }, far: { dist: 80, pitch: 57, lookY: 1 }, zoom: 0.5 };
type RigRange = Game['rig']['near'];
let savedCam: { near: RigRange; far: RigRange; zoom: number } | null = null;
let hudT = 0;
let weather: HTMLDivElement | null = null;

/** Zone setup (every zone): harbours remember themselves; the sea gets its script and scene dressing. */
export function sailingZoneSetup(game: Game, z: ZoneRuntime): void {
  const rt = setupSailingZone(game, z);
  if (rt && !z.opts.headless) rt.fx = new SeaScene(z, game.assets, rt);
}

function seaCamera(game: Game, on: boolean): void {
  const rig = game.rig;
  if (on && !savedCam) {
    savedCam = { near: rig.near, far: rig.far, zoom: rig.targetZoom };
    rig.near = { ...SEA_CAM.near };
    rig.far = { ...SEA_CAM.far };
    rig.targetZoom = SEA_CAM.zoom;
  } else if (!on && savedCam) {
    rig.near = savedCam.near;
    rig.far = savedCam.far;
    rig.targetZoom = savedCam.zoom;
    savedCam = null;
  }
}

/** Storm gloom and fog-bank mist at the screen edges, layered under the HUD. */
function setWeather(game: Game, storm: number, fog: number): void {
  if (!weather) {
    if (storm <= 0.01 && fog <= 0.01) return;
    weather = document.createElement('div');
    weather.className = 'sea-weather';
    weather.innerHTML = '<i class="sea-weather__storm"></i><i class="sea-weather__fog"></i>';
    game.hud.root.prepend(weather);
  }
  weather.style.setProperty('--storm', storm.toFixed(3));
  weather.style.setProperty('--fog', fog.toFixed(3));
}

export function sailingSystem(game: Game, dt: number): void {
  const rt = activeSea();
  const here = rt && rt.z === game.zone ? rt : null;
  const aboard = !!here?.ship;
  seaCamera(game, aboard);
  game.hud.root.classList.toggle('af-sea', aboard);
  if (here) here.flush();
  if (!here || here.disposed) {
    setWeather(game, 0, 0);
    if (seaHud.value) seaHud.value = null;
    return;
  }
  const fx = here.fx instanceof SeaScene ? here.fx : null;
  fx?.frame(dt, { camDist: game.rig.camera.position.distanceTo(game.rig.followPoint), fogSight: statsOf(shipOf(game.account)).fogSight });
  const storm = fx?.stormF ?? here.hazards.storm;
  const fog = fx?.fogF ?? here.hazards.fog;
  setWeather(game, storm, fog);
  hudT -= dt;
  if (hudT > 0) return;
  hudT = 1 / 15;
  const s = here.hudState();
  seaHud.value = s && !game.hud.root.classList.contains('hidden') ? { ...s, vis: { storm, fog }, voyage: here.time } : null;
}
